import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  AgentGroupDetail,
  AgentGroupRow,
  CreateAgentGroupRequest,
  UpdateAgentGroupRequest,
} from './types';

export const agentGroupKeys = {
  all: ['agent-groups'] as const,
  list: (userId: string | number | null) =>
    ['agent-groups', 'list', userId] as const,
  detail: (groupId: string | number) =>
    ['agent-groups', 'detail', groupId] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: agentGroupKeys.all });
}

/** GET /api/obd/group/agent/list/{userId} */
export function useAgentGroups() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: agentGroupKeys.list(userId),
    queryFn: () =>
      api.get<AgentGroupRow[]>(`/api/obd/group/agent/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<AgentGroupRow>(data),
  });
}

/** GET /api/obd/group/agent/{groupId} */
export function useAgentGroupDetail(groupId: string | number | null) {
  return useQuery({
    queryKey: agentGroupKeys.detail(groupId ?? 'none'),
    queryFn: () =>
      api.get<AgentGroupDetail>(`/api/obd/group/agent/${groupId}`),
    enabled: Boolean(groupId),
    staleTime: 30_000,
  });
}

export function useCreateAgentGroup() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (req: CreateAgentGroupRequest) =>
      api.post<{ message?: string }>('/api/obd/add/group/agent', req),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAgentGroup() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (req: UpdateAgentGroupRequest) =>
      api.post<{ message?: string }>('/api/obd/edit/group/agent', req),
    onSuccess: () => invalidate(),
  });
}

/**
 * Legacy used `POST /api/obd/group/agent/{groupId}` with `method: DELETE`
 * override. Real DELETE works the same way against this endpoint.
 */
export function useDeleteAgentGroup() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (groupId: number) =>
      apiClient
        .delete<{ message?: string }>(`/api/obd/group/agent/${groupId}`)
        .then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}
